<script setup lang="ts">
  defineOptions({
    name: 'MemberSuggestionItem',
  })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPText from '@ui/components/CPText.vue'

  import type { IConversationSuggestion } from '../../types/conversation.types'

  const props = defineProps({
    contact: {
      type: Object as PropType<IConversationSuggestion>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    click: [contact: IConversationSuggestion]
  }>()

  const { t } = useI18n()

  const suggestionId = computed(() => props.contact?.contactId || 'unknown')

  const handleClick = () => {
    if (!props.isLoading) {
      emit('click', props.contact)
    }
  }
</script>

<template>
  <div
    :id="`suggestion-item-${suggestionId}`"
    class="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-hover"
    :class="{ 'pointer-events-none opacity-50': isLoading }"
    :data-loading="isLoading"
    role="button"
    tabindex="0"
    @click="handleClick"
    @keyup.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Avatar -->
    <CPAvatar :src="contact.avatar" :title="contact.fullName || t('chats.user.defaultName')" class="h-12 w-12 flex-shrink-0" />

    <!-- Contact info -->
    <div class="min-w-0 flex-1">
      <CPText size="sm" weight="semibold" class="truncate text-body">
        {{ contact.fullName || t('chats.user.unknownUser') }}
      </CPText>
      <CPText v-if="contact.bio" size="xs" class="mt-0.5 truncate text-secondary">
        {{ contact.bio }}
      </CPText>
    </div>

    <!-- Loading spinner -->
    <div v-if="isLoading" class="flex-shrink-0">
      <div
        :id="`suggestion-item-loading-spinner-${suggestionId}`"
        class="h-5 w-5 animate-spin rounded-full border-2 border-default border-t-[var(--cp-primary-500)]"
      />
    </div>
  </div>
</template>
